/* eslint-disable react-refresh/only-export-components -- the scene library is bundled for the renderer, not hot-reloaded; countUpValue lives beside StatView on purpose (tested) */
/**
 * One component per scene kind. Each fills the frame, takes its colours from the brand theme by tone, keeps text in
 * the safe area and finishes its entrance within ~0.6 s. Scenes never know their neighbours; transitions are the
 * composition's job.
 */
import type { CSSProperties, ReactNode } from 'react';
import { AbsoluteFill, Easing, interpolate, spring, useCurrentFrame, useVideoConfig } from 'remotion';
import { BrandGround, CaptionLayerContext, CreatorCard, Packshot, KineticText, Logo, Media, MotifRule, Rise, SafeArea, Eyebrow, aspectOf, bodyStack, headingStack, palette, shouldFrame, useEnter, useMotif, useRegion, useSafeArea, useUnit } from './kit';
import { useContext } from 'react';
import { cueAt, type CaptionCue } from './captions';
import type { Caption, ClipScene, VideoSpec, EndCardScene, HookScene, ProductScene, QuoteScene, StatScene, TextLayer, TextScene, VideoScene, VideoTheme } from './spec';
import { mediaAspect, secondsToFrames } from './spec';
import type { VideoMedia } from './spec';

interface SceneProps<S> { scene: S; theme: VideoTheme; captionStyle: 'bold' | 'clean' }

/** Type size that shrinks gently with length, so a 3-word hook is huge and a 14-word line still fits. */
function fitSize(text: string, max: number, min: number, u: number): number {
  const chars = text.length;
  return Math.round(Math.max(min, Math.min(max, max - (chars - 12) * ((max - min) / 60))) * u);
}

/** A scene's ground — transparent beside the brand stage, where the stage's brand ground runs under every scene. */
function Ground({ color, children }: { color: string; children?: ReactNode }) {
  const region = useRegion();
  return <AbsoluteFill style={{ background: region.framed ? 'transparent' : color }}>{children}</AbsoluteFill>;
}

/**
 * Creator footage framed instead of cropped: one card, or two or three side by side (a split — the first is the one
 * heard, the others play muted). Used beside the brand stage, and whenever filling the box would zoom past the footage
 * rule (a vertical clip in a landscape frame). Off the stage, the brand ground fills the rest of the frame.
 */
function ClipCards({ media, others = [], voice, total, theme, line, lineSize, captions }: {
  media: VideoMedia; others?: VideoMedia[]; voice?: 'keep' | 'mute'; total: number; theme: VideoTheme; line?: string; lineSize?: number; captions?: ReactNode;
}) {
  const u = useUnit();
  const region = useRegion();
  const all = [media, ...others.slice(0, 2)];
  const ars = all.map((m) => mediaAspect(m) || 9 / 16);
  const gap = 32 * u;
  const top = region.height * 0.055;
  // As tall as the box allows (running off the bottom edge), narrower when the cards would not fit across.
  let h = region.height - top;
  const across = region.width * (all.length === 1 ? 0.9 : 0.94) - gap * (all.length - 1);
  if (ars.reduce((a, r) => a + r * h, 0) > across) h = across / ars.reduce((a, r) => a + r, 0);
  const bleed = h >= region.height - top - 1;
  const pal = palette(theme, 'dark');
  return (
    <AbsoluteFill>
      {!region.framed && <BrandGround theme={theme} />}
      <AbsoluteFill style={{ flexDirection: 'row', gap, justifyContent: 'center', alignItems: bleed ? 'flex-end' : 'center' }}>
        {all.map((m, i) => (
          <CreatorCard key={`${m.url}-${i}`} media={m} total={total} voice={i === 0 ? voice : 'mute'} height={h} theme={theme} highlight={all.length > 1 && i === 0} bleed={bleed}>
            {i === 0 && (line || captions) && (
              <div style={{ position: 'absolute', left: 0, right: 0, bottom: (bleed ? 64 : 36) * u, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 * u, padding: `0 ${28 * u}px` }}>
                {line && <Rise delay={4} style={{ background: pal.accent, color: pal.onAccent, fontFamily: headingStack(theme), fontWeight: 800, fontSize: (lineSize || 54) * u, lineHeight: 1.1, padding: `${12 * u}px ${22 * u}px`, borderRadius: 14 * u, textAlign: 'center', maxWidth: '100%' }}>{line}</Rise>}
                {captions}
              </div>
            )}
          </CreatorCard>
        ))}
      </AbsoluteFill>
    </AbsoluteFill>
  );
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function HookView({ scene, theme }: SceneProps<HookScene>) {
  const u = useUnit();
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const total = secondsToFrames(scene.duration);
  const motion = scene.motion || 'punch';
  const live = !!scene.media && scene.opening === 'live';
  const hasMedia = !!scene.media;
  const pal = palette(theme, hasMedia ? 'dark' : scene.tone || 'brand');
  const text = hasMedia && !live ? '#FFFFFF' : live ? pal.onAccent : pal.text;
  const size = fitSize(scene.text, live ? 128 : 176, live ? 80 : 100, u);
  // Motion of the whole line. punch/slam: the line is already there at frame 0 and lands; type: words appear one
  // every 2 frames from frame 0; rise: the classic float-in (empty first frame).
  const land = spring({ frame, fps, config: motion === 'slam' ? { damping: 9, mass: 0.6, stiffness: 220 } : { damping: 14, mass: 0.5, stiffness: 200 } });
  // Small starting scales: the line must fit the frame at frame 0 (it is the thumbnail), so it lands, never overflows.
  const scale = motion === 'slam' ? 1.12 - 0.12 * land : motion === 'punch' ? 1.05 - 0.05 * land : 1;
  const jolt = motion === 'slam' && frame > 3 && frame < 9 ? Math.sin(frame * 2.6) * 6 * u : 0;
  const shown = motion === 'type' ? Math.max(1, Math.floor(frame / 2) + 1) : Infinity;
  const line = motion === 'type' ? scene.text.split(/\s+/).slice(0, shown).join(' ') : scene.text;
  // Footage punch-in at the start (live openings): the clip is the hook, so it moves from frame 0 too.
  const mediaScale = interpolate(frame, [0, 10], [1.12, 1], { extrapolateRight: 'clamp', easing: Easing.out(Easing.cubic) });
  const words = (
    <KineticText text={line} size={size} color={text} accent={live ? '#FFFFFF' : pal.accent} onAccent={live ? '#111111' : pal.onAccent} font={headingStack(theme)} emphasis={scene.emphasis} stagger={2} instant={motion !== 'rise'} />
  );
  const region = useRegion();
  if (scene.media && shouldFrame(scene.media, region)) {
    return <ClipCards media={scene.media} voice={scene.voice} total={total} theme={theme} line={scene.text} lineSize={60} />;
  }
  return (
    <Ground color={pal.ground}>
      {scene.media && (live
        ? <AbsoluteFill style={{ transform: `scale(${mediaScale})` }}><Media media={scene.media} total={total} dim={0.25} voice={scene.voice} /></AbsoluteFill>
        : <Media media={scene.media} total={total} dim={1.25} blur={30} voice={scene.voice || 'mute'} />)}
      <SafeArea style={{ justifyContent: live ? (scene.place?.overlay === 'bottom' ? 'flex-end' : scene.place?.overlay === 'middle' ? 'center' : 'flex-start') : 'center' }}>
        <MotifRule color={pal.accent} u={u} />
        <div style={{ transform: `translateX(${jolt}px) scale(${scale})`, transformOrigin: 'left top', maxWidth: `${100 / 1.12}%`, ...(live ? { alignSelf: 'flex-start', background: pal.accent, padding: `${18 * u}px ${26 * u}px`, borderRadius: 20 * u, maxWidth: '100%' } : {}) }}>
          {words}
        </div>
        {scene.sub && <Rise delay={8} style={{ marginTop: 32 * u, fontFamily: bodyStack(theme), fontSize: 52 * u, fontWeight: 600, lineHeight: 1.3, color: hasMedia ? 'rgba(255,255,255,0.9)' : pal.muted, ...(live ? { textShadow: '0 2px 16px rgba(0,0,0,0.6)' } : {}) }}>{scene.sub}</Rise>}
      </SafeArea>
    </Ground>
  );
}

// ── Clip ──────────────────────────────────────────────────────────────────────

function activeCaption(captions: Caption[] | undefined, t: number): Caption | null {
  return (captions || []).find((c) => t >= c.start && t < c.end) || null;
}

function Captions({ captions, theme, style, accent, onAccent }: { captions?: Caption[]; theme: VideoTheme; style: 'bold' | 'clean'; accent: string; onAccent: string }) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const u = useUnit();
  const t = frame / fps;
  const c = activeCaption(captions, t);
  if (!c) return null;
  if (style === 'clean') {
    return (
      <div style={{ alignSelf: 'center', maxWidth: '92%', background: 'rgba(0,0,0,0.62)', color: '#FFFFFF', fontFamily: bodyStack(theme), fontWeight: 600, fontSize: 48 * u, lineHeight: 1.3, padding: `${14 * u}px ${26 * u}px`, borderRadius: 14 * u, textAlign: 'center' }}>{c.text}</div>
    );
  }
  const words = c.text.split(/\s+/).filter(Boolean);
  const per = (c.end - c.start) / Math.max(1, words.length);
  const at = Math.min(words.length - 1, Math.floor((t - c.start) / per));
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', columnGap: 18 * u, rowGap: 8 * u, fontFamily: headingStack(theme), fontWeight: 800, fontSize: 84 * u, lineHeight: 1.08, textTransform: 'uppercase', letterSpacing: '-0.01em' }}>
      {words.map((w, i) => (
        <span key={i} style={{ color: i === at ? onAccent : '#FFFFFF', background: i === at ? accent : 'transparent', borderRadius: 10 * u, padding: `0 ${10 * u}px`, transform: i === at ? 'scale(1.06)' : undefined, textShadow: i === at ? 'none' : '0 1px 3px rgba(0,0,0,0.7), 0 3px 14px rgba(0,0,0,0.55)', WebkitTextStroke: i === at ? undefined : `${2 * u}px rgba(0,0,0,0.55)`, paintOrder: 'stroke fill' }}>{w}</span>
      ))}
    </div>
  );
}

export function ClipView({ scene: given, theme, captionStyle }: SceneProps<ClipScene>) {
  const u = useUnit();
  // The captions layer draws every heard line (CaptionLayer): the clip keeps only its overlay.
  const layer = useContext(CaptionLayerContext);
  const scene = layer ? { ...given, captions: undefined } : given;
  const total = secondsToFrames(scene.duration);
  const pal = palette(theme, 'dark');
  const handle = scene.media.creatorHandle;
  // Placement from the layout pass (what the frames show); default: overlay top-left, captions at the bottom.
  const place = { overlay: scene.place?.overlay || 'top', side: scene.place?.side || 'left', captions: scene.place?.captions || 'bottom' };
  const align = place.side === 'center' ? 'center' : place.side === 'right' ? 'flex-end' : 'flex-start';
  const chip: CSSProperties = { alignSelf: 'flex-start', background: 'rgba(0,0,0,0.5)', color: '#FFFFFF', fontFamily: bodyStack(theme), fontWeight: 600, fontSize: 36 * u, padding: `${10 * u}px ${22 * u}px`, borderRadius: 999 };
  const overlay = scene.overlay ? (
    <Rise delay={4} style={{ alignSelf: align, background: pal.accent, color: pal.onAccent, fontFamily: headingStack(theme), fontWeight: 800, fontSize: 64 * u, lineHeight: 1.1, padding: `${14 * u}px ${26 * u}px`, borderRadius: 16 * u, maxWidth: '92%', textAlign: place.side === 'center' ? 'center' : place.side === 'right' ? 'right' : 'left' }}>{scene.overlay}</Rise>
  ) : null;
  const captions = <Captions captions={scene.captions} theme={theme} style={captionStyle} accent={pal.accent} onAccent={pal.onAccent} />;
  const slot = (band: 'top' | 'middle' | 'bottom') => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 * u, alignItems: 'stretch' }}>
      {band === 'top' && scene.showHandle !== false && handle && <Rise style={chip}>@{handle.replace(/^@/, '')}</Rise>}
      {place.overlay === band && overlay}
      {place.captions === band && !!scene.captions?.length && captions}
    </div>
  );
  const region = useRegion();
  if (shouldFrame(scene.media, region) || scene.others?.length) {
    return <ClipCards media={scene.media} others={scene.others} voice={scene.voice} total={total} theme={theme} line={scene.overlay} lineSize={44}
      captions={scene.captions?.length ? <Captions captions={scene.captions} theme={theme} style="clean" accent={pal.accent} onAccent={pal.onAccent} /> : undefined} />;
  }
  // The darker band follows the captions, so they read over any footage.
  const band = place.captions === 'top' ? 'linear-gradient(0deg, rgba(0,0,0,0) 52%, rgba(0,0,0,0.55) 78%, rgba(0,0,0,0.7) 100%)'
    : place.captions === 'middle' ? 'linear-gradient(180deg, rgba(0,0,0,0) 30%, rgba(0,0,0,0.5) 50%, rgba(0,0,0,0) 70%)'
    : 'linear-gradient(180deg, rgba(0,0,0,0) 48%, rgba(0,0,0,0.62) 72%, rgba(0,0,0,0.72) 100%)';
  return (
    <Ground color="#000000">
      <Media media={scene.media} total={total} dim={0.45} voice={scene.voice} />
      {!!scene.captions?.length && <AbsoluteFill style={{ background: band }} />}
      <SafeArea style={{ justifyContent: 'space-between' }}>
        {slot('top')}
        {slot('middle')}
        {slot('bottom')}
      </SafeArea>
    </Ground>
  );
}

/** A text on its own layer, over whatever plays under it — the same box as a clip's overlay, so a detached line
 * looks exactly as it did on its clip. */
export function TextLayerView({ layer, theme }: { layer: TextLayer; theme: VideoTheme }) {
  const u = useUnit();
  const pal = palette(theme, 'dark');
  const side = layer.side || 'left';
  const align = side === 'center' ? 'center' : side === 'right' ? 'flex-end' : 'flex-start';
  const band = layer.band || 'top';
  return (
    <SafeArea style={{ justifyContent: band === 'bottom' ? 'flex-end' : band === 'middle' ? 'center' : 'flex-start' }}>
      <Rise delay={2} style={{ alignSelf: align, background: pal.accent, color: pal.onAccent, fontFamily: headingStack(theme), fontWeight: 800, fontSize: 64 * u, lineHeight: 1.1, padding: `${14 * u}px ${26 * u}px`, borderRadius: 16 * u, maxWidth: '92%', textAlign: side === 'center' ? 'center' : side === 'right' ? 'right' : 'left' }}>{layer.text}</Rise>
    </SafeArea>
  );
}

/**
 * The captions layer: the heard line at this moment, in the video's caption style — bold (word by word, the word
 * being said lit in the accent, from the real word timings when we have them) or clean (one quiet line) — in the
 * band the layout pass chose for that scene, over a soft dark gradient so it reads on any footage.
 */
export function CaptionLayer({ spec, cues }: { spec: VideoSpec; cues: CaptionCue[] }) {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const u = useUnit();
  const t = frame / fps;
  const c = cueAt(cues, t);
  if (!c) return null;
  const theme = spec.theme;
  const pal = palette(theme, 'dark');
  const gradient = c.band === 'top' ? 'linear-gradient(0deg, rgba(0,0,0,0) 60%, rgba(0,0,0,0.5) 85%, rgba(0,0,0,0.62) 100%)'
    : c.band === 'middle' ? 'linear-gradient(180deg, rgba(0,0,0,0) 32%, rgba(0,0,0,0.42) 50%, rgba(0,0,0,0) 68%)'
    : 'linear-gradient(180deg, rgba(0,0,0,0) 58%, rgba(0,0,0,0.55) 80%, rgba(0,0,0,0.66) 100%)';
  let body: ReactNode;
  if ((spec.captionStyle || 'bold') === 'clean') {
    body = <div style={{ alignSelf: 'center', maxWidth: '92%', background: 'rgba(0,0,0,0.62)', color: '#FFFFFF', fontFamily: bodyStack(theme), fontWeight: 600, fontSize: 48 * u, lineHeight: 1.3, padding: `${14 * u}px ${26 * u}px`, borderRadius: 14 * u, textAlign: 'center' }}>{c.text}</div>;
  } else {
    const words = c.words?.length ? c.words.map((w) => w.w) : c.text.split(/\s+/).filter(Boolean);
    let at: number;
    if (c.words?.length) { at = 0; c.words.forEach((w, i) => { if (t >= w.s - 0.02) at = i; }); }
    else { const per = (c.end - c.start) / Math.max(1, words.length); at = Math.min(words.length - 1, Math.floor((t - c.start) / per)); }
    body = (
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', columnGap: 18 * u, rowGap: 8 * u, fontFamily: headingStack(theme), fontWeight: 800, fontSize: 84 * u, lineHeight: 1.08, textTransform: 'uppercase', letterSpacing: '-0.01em' }}>
        {words.map((w, i) => (
          <span key={i} style={{ color: i === at ? pal.onAccent : '#FFFFFF', background: i === at ? pal.accent : 'transparent', borderRadius: 10 * u, padding: `0 ${10 * u}px`, transform: i === at ? 'scale(1.06)' : undefined, textShadow: i === at ? 'none' : '0 1px 3px rgba(0,0,0,0.7), 0 3px 14px rgba(0,0,0,0.55)', WebkitTextStroke: i === at ? undefined : `${2 * u}px rgba(0,0,0,0.55)`, paintOrder: 'stroke fill' }}>{w}</span>
        ))}
      </div>
    );
  }
  return (
    <AbsoluteFill style={{ pointerEvents: 'none' }}>
      <AbsoluteFill style={{ background: gradient }} />
      <SafeArea style={{ justifyContent: c.band === 'top' ? 'flex-start' : c.band === 'middle' ? 'center' : 'flex-end' }}>{body}</SafeArea>
    </AbsoluteFill>
  );
}

// ── Text ──────────────────────────────────────────────────────────────────────

export function TextView({ scene, theme }: SceneProps<TextScene>) {
  const u = useUnit();
  const pal = palette(theme, scene.tone || 'dark');
  const bar = useEnter(2);
  const motif = useMotif();
  const words = scene.text.split(/\s+/).length;
  return (
    <Ground color={pal.ground}>
      <SafeArea style={{ justifyContent: 'center', gap: 32 * u }}>
        {scene.eyebrow && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 * u }}>
            <Eyebrow text={scene.eyebrow} theme={theme} color={pal.accent === pal.text ? pal.muted : pal.accent} size={36 * u} />
            {motif === 'rule' && <div style={{ width: 140 * u * bar, height: 10 * u, borderRadius: 4 * u, background: pal.accent }} />}
          </div>
        )}
        {!scene.eyebrow && <MotifRule color={pal.accent} u={u} />}
        <KineticText text={scene.text} size={fitSize(scene.text, 170, 96, u)} color={pal.text} accent={pal.accent} onAccent={pal.onAccent} font={headingStack(theme)} emphasis={scene.emphasis} delay={scene.eyebrow ? 4 : 0} />
        {scene.sub && <Rise delay={6 + words * 3} style={{ fontFamily: bodyStack(theme), fontSize: 52 * u, lineHeight: 1.35, color: pal.muted, maxWidth: 940 * u }}>{scene.sub}</Rise>}
      </SafeArea>
    </Ground>
  );
}

// ── Stat ──────────────────────────────────────────────────────────────────────

/**
 * "2.4M" or "250,000" settles up to its value and keeps its prefix and suffix. Only magnitudes count (≥ 1,000 or a
 * K/M/B suffix): a small number, a percentage or a spec ("10 ans", "99,99 %", "16 g") just appears — a frame showing
 * "9 ans" on a 10-year guarantee reads as a wrong fact. A comma before 1–2 final digits is a decimal comma ("99,99").
 */
export function countUpValue(value: string, frame: number, frames: number): string {
  const m = value.match(/^([^\d-]*)(-?[\d.,]+)(.*)$/);
  if (!m) return value;
  const decimalComma = /,\d{1,2}$/.test(m[2]) && !/\.\d/.test(m[2]);
  const raw = decimalComma ? m[2].replace(/[.\s]/g, '').replace(',', '.') : m[2].replace(/,/g, '');
  const target = Number(raw);
  if (!Number.isFinite(target)) return value;
  const magnitude = Math.abs(target) >= 1000 || /^\s?[kKmMbB]/.test(m[3]);
  if (!magnitude || m[3].trim().startsWith('%')) return value;
  const decimals = (raw.split('.')[1] || '').length;
  // A short settle from 80% of the value, not a count from zero: a long count reads as the figure changing.
  const p = interpolate(frame, [0, frames], [0.8, 1], { extrapolateRight: 'clamp', easing: Easing.out(Easing.cubic) });
  if (p >= 1) return value;
  const n = target * p;
  let shown = m[2].includes(',') && !decimalComma ? n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) : n.toFixed(decimals);
  if (decimalComma) shown = shown.replace('.', ',');
  return `${m[1]}${shown}${m[3]}`;
}

function useCountUp(value: string, frames: number): string {
  return countUpValue(value, useCurrentFrame(), frames);
}

export function StatView({ scene, theme }: SceneProps<StatScene>) {
  const u = useUnit();
  const { width } = useVideoConfig();
  const total = secondsToFrames(scene.duration);
  const pal = palette(theme, scene.media ? 'dark' : scene.tone || 'dark');
  const shown = useCountUp(scene.value, Math.min(16, Math.round(total * 0.25)));
  const p = useEnter(0, 14);
  // The number fills the safe width at most (~0.66 em per glyph in a heavy display face), never past it.
  const safe = useSafeArea();
  const room = width - safe.left - safe.right;
  const big = Math.round(Math.min(360 * u, room / (Math.max(3, scene.value.length) * 0.66)));
  return (
    <Ground color={pal.ground}>
      {scene.media && <Media media={scene.media} total={total} dim={1} />}
      <SafeArea style={{ justifyContent: 'center', gap: 20 * u }}>
        <MotifRule color={pal.text} u={u} />
        <div style={{ fontFamily: headingStack(theme), fontWeight: 800, fontSize: big, lineHeight: 0.92, letterSpacing: '-0.05em', color: pal.accent, transform: `scale(${0.85 + 0.15 * p})`, transformOrigin: 'left bottom', fontVariantNumeric: 'tabular-nums' }}>{shown}</div>
        <Rise delay={8} style={{ fontFamily: headingStack(theme), fontWeight: 700, fontSize: 84 * u, lineHeight: 1.08, color: scene.media ? '#FFFFFF' : pal.text, maxWidth: 940 * u }}>{scene.label}</Rise>
        {scene.sub && <Rise delay={14} style={{ fontFamily: bodyStack(theme), fontSize: 48 * u, lineHeight: 1.35, color: scene.media ? 'rgba(255,255,255,0.8)' : pal.muted, maxWidth: 940 * u }}>{scene.sub}</Rise>}
      </SafeArea>
    </Ground>
  );
}

// ── Quote ─────────────────────────────────────────────────────────────────────

export function QuoteView({ scene, theme }: SceneProps<QuoteScene>) {
  const u = useUnit();
  const total = secondsToFrames(scene.duration);
  const pal = palette(theme, scene.media ? 'dark' : scene.tone || 'light');
  const mark = useEnter(0, 12);
  const size = fitSize(scene.quote, 108, 64, u);
  return (
    <Ground color={pal.ground}>
      {scene.media && <Media media={scene.media} total={total} dim={1} />}
      <SafeArea style={{ justifyContent: 'center', gap: 28 * u }}>
        <div style={{ fontFamily: headingStack(theme), fontWeight: 800, fontSize: 300 * u, lineHeight: 0.6, height: 150 * u, color: pal.accent, opacity: mark, transform: `translateY(${(1 - mark) * 30}px)` }}>“</div>
        <KineticText text={scene.quote} size={size} weight={600} lineHeight={1.18} color={scene.media ? '#FFFFFF' : pal.text} accent={pal.accent} onAccent={pal.onAccent} font={headingStack(theme)} stagger={1} delay={4} />
        {scene.author && <Rise delay={14} style={{ fontFamily: bodyStack(theme), fontSize: 44 * u, fontWeight: 600, color: scene.media ? 'rgba(255,255,255,0.8)' : pal.muted }}>— {scene.author}</Rise>}
      </SafeArea>
    </Ground>
  );
}

// ── Product ───────────────────────────────────────────────────────────────────

export function ProductView({ scene, theme }: SceneProps<ProductScene>) {
  const u = useUnit();
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();
  const total = secondsToFrames(scene.duration);
  // Product shots are mostly packshots on white: they sit on a light or brand ground (Packshot keeps their true
  // colours). A dark ground would leave a white box, so it is never used here.
  const pal = palette(theme, scene.tone === 'brand' ? 'brand' : 'light');
  const landscape = aspectOf(width, height) === '16:9';
  const p = useEnter(0, 15);
  const float = Math.sin((frame / 30) * 1.4) * 6 * u;
  const media = { ...scene.media, fit: scene.media.fit || 'contain' } as typeof scene.media;
  const picture = (
    // The transform isolates this layer: it carries the scene's ground, so the multiply blend dissolves the
    // packshot's white into the same colour as the page (on a transparent layer the white box would show).
    <div style={{ position: 'relative', flex: landscape ? '0 0 50%' : '0 0 64%', background: pal.ground, transform: `translateY(${float}px) scale(${1 + 0.08 * p}) rotate(${-7 + 3 * p}deg)`, opacity: p }}>
      {/* Depth: a warm halo behind and a contact shadow under the product, so a packshot sits in space, not on a sheet. */}
      <div style={{ position: 'absolute', inset: '10%', borderRadius: '50%', background: pal.accent, opacity: 0.22, filter: `blur(${70 * u}px)` }} />
      <div style={{ position: 'absolute', left: '18%', right: '18%', bottom: '14%', height: '7%', borderRadius: '50%', background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 70%)', filter: `blur(${8 * u}px)` }} />
      {/* True colours: melted into a near-white ground, on a light plate on a coloured one (Packshot). */}
      <Packshot media={media} ground={pal.ground} total={total} height="100%" />
    </div>
  );
  const words = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 * u, justifyContent: 'center', flex: 1, minWidth: 0 }}>
      <MotifRule color={pal.accent} u={u} delay={6} />
      <KineticText text={scene.name} size={fitSize(scene.name, landscape ? 112 : 132, 80, u)} color={pal.text} accent={pal.accent} onAccent={pal.onAccent} font={headingStack(theme)} delay={6} />
      {scene.line && <Rise delay={14} style={{ fontFamily: bodyStack(theme), fontSize: 50 * u, lineHeight: 1.35, color: pal.muted }}>{scene.line}</Rise>}
    </div>
  );
  return (
    <Ground color={pal.ground}>
      {/* The picture is scaled and tilted, so the landscape gap leaves room for its swing. */}
      <SafeArea style={{ flexDirection: landscape ? 'row' : 'column', gap: (landscape ? 110 : 48) * u }}>
        {landscape ? <>{words}{picture}</> : <>{picture}{words}</>}
      </SafeArea>
    </Ground>
  );
}

// ── End card ──────────────────────────────────────────────────────────────────

export function EndCardView({ scene, theme }: SceneProps<EndCardScene>) {
  const u = useUnit();
  const { width, height } = useVideoConfig();
  const pal = palette(theme, scene.tone || 'dark');
  const logo = useEnter(0, 12);
  const cta = useEnter(14, 12);
  const total = secondsToFrames(scene.duration);
  const picture = scene.media?.kind === 'image' ? scene.media : null;
  // Landscape: the product beside the words (a portrait stack does not fit 1080 px of height).
  const landscape = aspectOf(width, height) === '16:9';
  const logoEl = (
    <div style={{ transform: `scale(${0.8 + 0.2 * logo})`, opacity: logo, color: pal.text }}>
      <Logo theme={theme} onDark={pal.dark} height={(landscape ? 110 : picture ? 120 : 170) * u} style={landscape ? { maxWidth: Math.round(width * 0.4) } : undefined} />
    </div>
  );
  const rest = (
    <>
      {scene.headline && <KineticText text={scene.headline} size={fitSize(scene.headline, landscape ? 96 : 116, landscape ? 60 : 76, u)} color={pal.text} accent={pal.accent} onAccent={pal.onAccent} font={headingStack(theme)} align="center" delay={6} />}
      {scene.cta && (
        <div style={{ opacity: cta, transform: `translateY(${(1 - cta) * 20}px) scale(${0.94 + 0.06 * cta})`, background: pal.accent, color: pal.onAccent, fontFamily: headingStack(theme), fontWeight: 800, fontSize: (landscape ? 50 : 60) * u, padding: `${22 * u}px ${56 * u}px`, borderRadius: 999 }}>{scene.cta}</div>
      )}
      {/* No button (TV, where nothing is clicked): the address IS the call to action, so it reads like one. */}
      {scene.url && <Rise delay={20} style={scene.cta
        ? { fontFamily: bodyStack(theme), fontSize: 40 * u, color: pal.muted, letterSpacing: '0.02em' }
        : { fontFamily: bodyStack(theme), fontSize: 54 * u, fontWeight: 700, color: pal.text, letterSpacing: '0.01em' }}>{scene.url}</Rise>}
    </>
  );
  const product = picture && (
    // The product, big, next to the button: what to buy is on screen when the viewer decides.
    <div style={{ width: landscape ? '44%' : 640 * u, height: landscape ? '78%' : 520 * u, opacity: logo, transform: `translateY(${(1 - logo) * 30}px) rotate(${-4 + 4 * logo}deg)` }}>
      <Packshot media={picture} ground={pal.ground} total={total} height="100%" />
    </div>
  );
  return (
    <Ground color={pal.ground}>
      {landscape ? (
        <SafeArea style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 80 * u }}>
          {product}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 30 * u, textAlign: 'center', flex: 1 }}>{logoEl}{rest}</div>
        </SafeArea>
      ) : (
        <SafeArea style={{ justifyContent: 'center', alignItems: 'center', gap: 44 * u, textAlign: 'center' }}>{logoEl}{product}{rest}</SafeArea>
      )}
    </Ground>
  );
}

export function SceneView({ scene, theme, captionStyle }: SceneProps<VideoScene>) {
  switch (scene.kind) {
    case 'hook': return <HookView scene={scene} theme={theme} captionStyle={captionStyle} />;
    case 'clip': return <ClipView scene={scene} theme={theme} captionStyle={captionStyle} />;
    case 'text': return <TextView scene={scene} theme={theme} captionStyle={captionStyle} />;
    case 'stat': return <StatView scene={scene} theme={theme} captionStyle={captionStyle} />;
    case 'quote': return <QuoteView scene={scene} theme={theme} captionStyle={captionStyle} />;
    case 'product': return <ProductView scene={scene} theme={theme} captionStyle={captionStyle} />;
    case 'endcard': return <EndCardView scene={scene} theme={theme} captionStyle={captionStyle} />;
    default: return null;
  }
}
